import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Users, Wallet, Clock, MapPin, TrendingUp, HandCoins } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const naira = (n) => `₦${(n || 0).toLocaleString()}`;

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [board, setBoard] = useState({ lgas: [], wards: [] });

  useEffect(() => {
    if (user?.role === "media_admin") return;
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/leaderboard").then((r) => setBoard(r.data)).catch(() => {});
  }, [user]);

  if (user?.role === "media_admin") return <Navigate to="/admin/press" replace />;

  const cards = [
    { icon: Users, label: "Total Members", value: stats?.total_members ?? "—", color: "text-primary bg-primary/10" },
    { icon: Clock, label: "Pending Rewards", value: stats?.pending_rewards ?? "—", color: "text-amber-600 bg-amber-100" },
    { icon: Wallet, label: "Disbursed", value: stats?.disbursed_rewards ?? "—", color: "text-emerald-600 bg-emerald-100" },
    { icon: HandCoins, label: "Total Disbursed", value: naira(stats?.total_disbursed), color: "text-accent bg-red-100" },
    { icon: Users, label: "Volunteers", value: stats?.volunteers ?? "—", color: "text-sky-600 bg-sky-100" },
    { icon: MapPin, label: "Active LGAs", value: stats?.active_lgas ?? "—", color: "text-violet-600 bg-violet-100" },
  ];
  const COLORS = ["#008751", "#FFD700", "#E20015", "#0ea5e9", "#8b5cf6", "#f59e0b"];

  return (
    <div>
      <div className="mb-8">
        <h1 className="font-heading font-black text-3xl">Dashboard</h1>
        <p className="text-muted-foreground">{user?.role === "super_admin" ? "Statewide overview across all 13 LGAs." : `Overview for ${user?.assigned_lga} LGA.`}</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
        {cards.map((c, i) => (
          <div key={i} data-testid={`stat-${i}`} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
            <div className={`w-11 h-11 rounded-xl flex items-center justify-center mb-3 ${c.color}`}><c.icon className="w-5 h-5" /></div>
            <div className="font-heading font-black text-2xl md:text-3xl">{c.value}</div>
            <div className="text-sm text-muted-foreground">{c.label}</div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
          <div className="flex items-center gap-2 mb-4"><TrendingUp className="w-5 h-5 text-primary" /><h3 className="font-heading font-bold text-lg">LGA Leaderboard</h3></div>
          {board.lgas.length ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={board.lgas} layout="vertical" margin={{ left: 10 }}>
                <XAxis type="number" hide /><YAxis dataKey="lga" type="category" width={90} tick={{ fontSize: 12 }} />
                <Tooltip cursor={{ fill: "#f1f5f9" }} />
                <Bar dataKey="count" radius={[0, 6, 6, 0]}>{board.lgas.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}</Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : <div className="text-sm text-muted-foreground py-12 text-center">No member data yet.</div>}
        </div>

        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
          <h3 className="font-heading font-bold text-lg mb-4">Top Wards</h3>
          {board.wards.length ? (
            <div className="space-y-2">
              {board.wards.map((w, i) => (
                <div key={i} className="flex items-center justify-between py-2 border-b border-slate-50 last:border-0">
                  <div className="flex items-center gap-3">
                    <span className="w-7 h-7 rounded-lg bg-primary/10 text-primary font-bold text-sm flex items-center justify-center">{i + 1}</span>
                    <div><div className="font-semibold text-sm">{w.ward}</div><div className="text-xs text-muted-foreground">{w.lga}</div></div>
                  </div>
                  <span className="font-heading font-bold text-primary">{w.count}</span>
                </div>
              ))}
            </div>
          ) : <div className="text-sm text-muted-foreground py-12 text-center">No member data yet.</div>}
        </div>
      </div>
    </div>
  );
}
